import {
  CSSProperties,
  FocusEvent,
  MouseEvent,
  ReactNode,
  useState,
} from 'react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { Input, InputNumber, Tooltip, theme } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { maskInputField } from '@/utils/common/mask-input.utils';
import { ErrorText } from '../Elements';
import FormLabel from './FormLabel';

const { useToken } = theme;

interface InputFieldProps<TFieldValues extends FieldValues> {
  label?: string;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  control: any;
  name: Path<TFieldValues>;
  error?: string;
  readonly?: boolean;
  disabled?: boolean;
  variant?: 'outlined' | 'borderless' | 'filled';
  prefix?: string | ReactNode;
  suffix?: string | ReactNode;
  size?: 'large' | 'middle' | 'small';
  formDirection?: 'row' | 'column';
  required?: boolean;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  visibilityToggle?: {
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
  };
  inputStyles?: CSSProperties;
  rootStyles?: CSSProperties;
  max?: number;
  onChange?: (value: any) => void;
  /**
   * Masks the displayed value behind an eye toggle (for sensitive values such
   * as account numbers). The input stays editable — it unmasks while focused
   * and while the eye is on — so this is display masking, not a password
   * field: no `type="password"`, so browsers and password managers leave it
   * alone. `text` inputs only.
   */
  hideInput?: boolean;
}

const InputField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  type = 'text',
  placeholder,
  variant = 'outlined',
  readonly = false,
  disabled = false,
  error,
  prefix,
  suffix,
  size = 'middle',
  formDirection = 'column',
  required = false,
  onBlur,
  visibilityToggle,
  inputStyles = { width: '100%', color: '#000' },
  rootStyles,
  max,
  onChange,
  hideInput = false,
}: InputFieldProps<TFieldValues>) => {
  const errorStatus = error ? 'error' : undefined;
  const { token } = useToken();

  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  // Masked at rest only — you can't type into a value you can't read.
  const isMasked = hideInput && !revealed && !focused;

  const EyeIcon = revealed ? EyeInvisibleOutlined : EyeOutlined;
  const toggleLabel = `${revealed ? 'Hide' : 'Show'} ${label ?? 'value'}`;

  // Overlaid rather than passed as `suffix` on purpose: a suffix makes antd
  // wrap the input in `.ant-input-affix-wrapper`, and any page that compacts
  // `.ant-input` by height then sizes the inner input while the wrapper adds
  // its own padding on top — leaving the field taller than its neighbours.
  // Floating the icon keeps this a plain `.ant-input`, so it inherits whatever
  // sizing the surrounding page already applies.
  const eyeToggle = (
    <Tooltip title={toggleLabel}>
      <EyeIcon
        role="button"
        aria-label={toggleLabel}
        style={{
          position: 'absolute',
          insetInlineEnd: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'pointer',
          color: token.colorTextTertiary,
        }}
        // Keep focus in the input so toggling doesn't interrupt typing.
        onMouseDown={(e: MouseEvent) => e.preventDefault()}
        onClick={() => setRevealed((prev) => !prev)}
      />
    </Tooltip>
  );

  return (
    <div className={`form-control ${formDirection}`} style={rootStyles}>
      {label && <FormLabel required={required} label={label} />}
      <div className="input-containter">
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            // const handleChange = (value: any) => {
            //   field.onChange(value);
            //   onChange?.(value);
            // };
            if (type === 'password') {
              return (
                <Input.Password
                  {...field}
                  prefix={prefix}
                  suffix={suffix}
                  type={type}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                  visibilityToggle={visibilityToggle}
                />
              );
            } else if (type === 'number') {
              return (
                <InputNumber
                  {...field}
                  prefix={prefix}
                  suffix={suffix}
                  type={type}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onBlur={onBlur}
                  style={inputStyles}
                  max={max}
                />
              );
            } else {
              const input = (
                <Input
                  {...field}
                  // The mask is length-preserving, so the caret still lands
                  // where the analyst clicks.
                  value={isMasked ? maskInputField(field.value) : field.value}
                  prefix={prefix}
                  suffix={suffix}
                  type={type}
                  readOnly={readonly}
                  variant={variant}
                  placeholder={placeholder}
                  status={errorStatus}
                  size={size}
                  disabled={disabled}
                  onFocus={hideInput ? () => setFocused(true) : undefined}
                  onBlur={
                    hideInput
                      ? (e) => {
                          setFocused(false);
                          field.onBlur();
                          onBlur?.(e);
                        }
                      : onBlur
                  }
                  style={
                    hideInput
                      ? // Room for the overlaid eye.
                        { ...inputStyles, paddingInlineEnd: 26 }
                      : inputStyles
                  }
                />
              );

              if (!hideInput) return input;

              return (
                <div style={{ position: 'relative', width: '100%' }}>
                  {input}
                  {eyeToggle}
                </div>
              );
            }
          }}
        />
        {error && <ErrorText error={error} />}
      </div>
    </div>
  );
};

export default InputField;
